import { Component, OnInit, ViewChild, ElementRef, Output, EventEmitter, Input, NgZone } from '@angular/core';
import { saveAs } from 'file-saver';
import { environment } from '../../../environments/environment';

declare const google: any;

@Component({
  selector: 'app-google-map',
  templateUrl: './google-map.component.html',
  styleUrls: ['./google-map.component.scss']
})
export class GoogleMapComponent implements OnInit {

  @Input() lat: number;
  @Input() lng: number;
  @Input() showSnapshotBtn: boolean = false;
  @Input() clickable: boolean = false;
  @Input() singleMarker: boolean = false;

  @ViewChild('map') mapContainer: ElementRef;

  @Output() onMapReady: EventEmitter<any> = new EventEmitter();
  @Output() onMarkerClick: EventEmitter<{lat: number, lng: number}> = new EventEmitter();
  @Output() onMapClick: EventEmitter<{lat: number, lng: number}> = new EventEmitter();

  public googleMap: any = null;
  public markers: any[] = [];
  public polylines: any[] = [];
  public clickListener: any = null;

  private static scriptLoaded = false;
  private static scriptLoading = false;
  private static scriptLoadPromise: Promise<void> = null;

  private readonly apiKey = environment.googleMapsApiKey;

  constructor(private ngZone: NgZone) { }

  ngOnInit() {
    this.loadGoogleMapsScript()
      .then(() => {
        this.initializeMap();
      })
      .catch(() => {});
  }

  private loadGoogleMapsScript(): Promise<void> {
    if (GoogleMapComponent.scriptLoaded) {
      return Promise.resolve();
    }

    if (GoogleMapComponent.scriptLoading) {
      return GoogleMapComponent.scriptLoadPromise;
    }

    GoogleMapComponent.scriptLoading = true;
    GoogleMapComponent.scriptLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        GoogleMapComponent.scriptLoaded = true;
        GoogleMapComponent.scriptLoading = false;
        resolve();
      };

      script.onerror = () => {
        GoogleMapComponent.scriptLoading = false;
        reject(new Error('Google Maps script failed to load'));
      };

      document.head.appendChild(script);
    });

    return GoogleMapComponent.scriptLoadPromise;
  }

  initializeMap() {
    const defaultLocation = {
      lat: this.lat || 36.876874,
      lng: this.lng || 34.063730
    };

    this.googleMap = new google.maps.Map(this.mapContainer.nativeElement, {
      center: defaultLocation,
      zoom: 4,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_BOTTOM
      },
      scrollwheel: true,
      gestureHandling: 'greedy',
      disableDoubleClickZoom: true
    });

    if (this.clickable) {
      this.enableMapClick();
    }

    this.ngZone.run(() => {
      this.onMapReady.emit(this.googleMap);
    });

    if (this.lat && this.lng) {
      this.addMarkerToMap([{ lat: this.lat, lng: this.lng }]);
    }
  }

  enableMapClick() {
    if (this.clickListener) {
      google.maps.event.removeListener(this.clickListener);
    }

    this.googleMap.setOptions({ draggableCursor: 'crosshair' });

    this.clickListener = this.googleMap.addListener('click', (event: any) => {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      if (this.singleMarker) {
        this.removeMarkers();
      }

      this.addMarkerToMap([{ lat, lng }]);

      this.ngZone.run(() => {
        this.onMapClick.emit({ lat, lng });
      });
    });
  }

  takeSnapshot() {
    if (!this.googleMap) return;

    const center = this.googleMap.getCenter();
    const zoom = this.googleMap.getZoom();
    const size = `${this.mapContainer.nativeElement.offsetWidth}x${this.mapContainer.nativeElement.offsetHeight}`;

    let url = `https://maps.googleapis.com/maps/api/staticmap?`;
    url += `center=${center.lat()},${center.lng()}`;
    url += `&zoom=${zoom}`;
    url += `&size=${size}`;
    url += `&key=${this.apiKey}`;

    this.markers.forEach(marker => {
      const pos = marker.getPosition();
      url += `&markers=${pos.lat()},${pos.lng()}`;
    });

    this.polylines.forEach(polyline => {
      const path = polyline.getPath();
      url += `&path=color:0xff0000ff|weight:4`;
      path.forEach((point: any) => {
        url += `|${point.lat()},${point.lng()}`;
      });
    });

    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        saveAs(blob, 'Map.png');
      })
      .catch(() => {});
  }

  addMarkerToMap(coords: Array<{lat: number, lng: number, label?: string, icon?: string, draggable?: boolean}>) {
    if (this.singleMarker) {
      this.removeMarkers();
    }

    coords.forEach(coord => {
      const marker = new google.maps.Marker({
        position: { lat: coord.lat, lng: coord.lng },
        map: this.googleMap,
        label: coord.label,
        icon: coord.icon,
        draggable: coord.draggable || false
      });

      marker.addListener('click', () => {
        this.ngZone.run(() => {
          this.onMarkerClick.emit({
            lat: marker.getPosition().lat(),
            lng: marker.getPosition().lng()
          });
        });
      });

      if (coord.draggable) {
        marker.addListener('dragend', (event: any) => {
          const lat = event.latLng.lat();
          const lng = event.latLng.lng();
          this.ngZone.run(() => {
            this.onMapClick.emit({ lat, lng });
          });
        });
      }

      this.markers.push(marker);
    });

    if (coords.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      coords.forEach(coord => {
        bounds.extend(new google.maps.LatLng(coord.lat, coord.lng));
      });
      this.googleMap.fitBounds(bounds);
    } else if (coords.length === 1) {
      this.googleMap.setCenter({ lat: coords[0].lat, lng: coords[0].lng });
      this.googleMap.setZoom(9);
    }
  }

  removeMarkers() {
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];
  }

  setCenter(lat: number, lng: number) {
    if (this.googleMap) {
      this.googleMap.setCenter({ lat, lng });
    }
  }

  setZoom(zoom: number) {
    if (this.googleMap) {
      this.googleMap.setZoom(zoom);
    }
  }
}



// WEBPACK FOOTER //
// /var/www/bizisystem-frontend/src/src/app/shared/google-map/google-map.component.ts